import { fragment, icon, node, text } from "~/util/dom";
import { _ } from "~/route/globals";
import PopOver from "~/ui/popover";
import createSDSIcon from "~/ui/icon";
import { CHEVRON_RIGHT, ROUTE_USER_SPEED } from "~/ui/sds-compatible-icons";
import { store } from "~/state/store";
import { ESETTINGS_KEYS, IUserMaxSpeedData } from "~/state/user/interfaces";
import { Unsubscribe } from "redux";
import { watch, watchSettingsUserSpeedChange } from "~/state/util";
import { SettingsUserSpeedPopup } from "~/profile/settings/userspeed/popup";

function openUserSpeedSettings() {
	const popup = new SettingsUserSpeedPopup(() => {
		popup.destroy();
	});

	popup.open();
}

function createUserSpeedLine(maxSpeed, openCallback: () => void) {
	const container = fragment();
	const content = node("div", { className: "route-car-options__buttonTextLine" });
	const title = node("span", { className: "route-car-options__buttonTextLine__title" }, _("route.params.options.userSpeedTitle"));
	const signOn = node("span", { className: "route-car-options__buttonTextLine__signOn" }, _("on"));
	const description = node("span", { className: "route-car-options__buttonTextLine__description" }, _("route.params.options.userSpeedDescription"));
	const chevronIcon = createSDSIcon({ symbol: CHEVRON_RIGHT });
	const iconCont = node("div");

	content.append(title);
	if (maxSpeed?.car?.maxSpeedEnabled) {
		content.append(signOn);
	}
	content.append(description);

	content.addEventListener("click", () => {
		openCallback();
		openUserSpeedSettings();
	});

	chevronIcon.addEventListener("click", () => {
		openCallback();
		openUserSpeedSettings();
	});

	iconCont.append(chevronIcon);

	container.append(content, iconCont);

	return container;
}

export default class ParamsOptions {
	#container: HTMLDivElement;

	#label: HTMLDivElement;

	#popup: PopOver;

	#openState: boolean;

	#toggleButton: HTMLButtonElement;

	#iconClosed: SVGSVGElement | HTMLSpanElement;

	#iconOpen: SVGSVGElement | HTMLSpanElement;

	#labelIcon: HTMLSpanElement;

	#content: HTMLDivElement;

	#userSpeedSetting: IUserMaxSpeedData;

	#storeUnsubscribeUserSpeed: Unsubscribe;

	constructor() {
		this.#container = node("div", { className: "route-options" });

		this.#label = node("div", { className: "params-select-label" });
		this.#labelIcon = node("span", { className: "params-select-label__iconHolder" });
		this.#toggleButton = node("button");
		this.#iconClosed = icon("forward");
		this.#iconOpen = icon("dropdown");

		this.#content = this.#build();

		const { settings, user } = store.getState().user;

		this.#userSpeedSetting = user.premium ? (settings.find(item => item.title === ESETTINGS_KEYS.ROUTING_CRITERION_SPEED)?.data?.value || {}) as IUserMaxSpeedData : {};

		this.#toggleButton.appendChild(this.#iconClosed);
		this.#label.append(
			this.#toggleButton,
			text(_("options")),
			this.#labelIcon,
		);

		this.#popup = new PopOver({
			anchor: this.#label,
			content: this.#content,
			className: "params-select-popup car-options",
			width: 302,
			onClose: () => {
				this.#close();
			},
			offset: 8,
		});

		this.#label.addEventListener("click", this, false);
		this.#container.append(this.#label);

		Mapy.CML.addListener(params => {
			this.#updateLabelIcon(params.criterion);
		}, "route.params");

		const wtUserSpeed = watch(store.getState, "user.settings", watchSettingsUserSpeedChange);

		this.#storeUnsubscribeUserSpeed = store.subscribe(wtUserSpeed(newValue => {
			const actualUser = store.getState().user;

			this.#userSpeedSetting = (actualUser.user.premium ? newValue.find(item => item.title === ESETTINGS_KEYS.ROUTING_CRITERION_SPEED)?.data?.value || {} : null) as IUserMaxSpeedData;
			this.#updateLabelIcon();
		}));
	}

	destroy() {
		this.#close();
		this.#storeUnsubscribeUserSpeed();
		this.#toggleButton.removeEventListener("click", this, false);
		this.#container.remove();
		this.#container = null;
	}

	handleEvent(event: Event) {
		const target = event.target as Node;

		if (this.#label.contains(target) || this.#label === target) {
			if (this.#openState) {
				this.#popup.close();
			} else {
				this.#open();
			}
		}
	}

	get input() {
		return this.#container;
	}

	#build() {
		const container = node("div", { className: "route-car-options" });
		const dynamicOptionsUserSpeed = node("div", { className: "route-car-options__userSpeed" });
		const title = node("h4", { className: "route-car-options__title", textContent: _("options") });

		container.append(title);
		container.append(dynamicOptionsUserSpeed);

		dynamicOptionsUserSpeed.append(createUserSpeedLine(this.#userSpeedSetting, () => {
			this.#popup.close();
		}));

		this.#updateLabelIcon();

		return container;
	}

	#open() {
		this.#iconClosed.remove();
		this.#toggleButton.insertBefore(this.#iconOpen, this.#toggleButton.firstChild);

		this.#popup.open();

		this.#label.classList.add("open");

		this.#openState = true;
	}

	#close() {
		this.#iconOpen.remove();
		this.#toggleButton.insertBefore(this.#iconClosed, this.#toggleButton.firstChild);

		this.#label.classList.remove("open");

		this.#openState = false;
	}

	#updateLabelIcon(criterion = Mapy.CML.state.route?.params?.criterion || 111) {
		const criterionSet = this.#userSpeedSetting ? Object.keys(this.#userSpeedSetting) : [];

		this.#labelIcon.innerHTML = "";

		if (criterionSet.includes(criterion.toString())) {
			this.#labelIcon.append(createSDSIcon({
				symbol: ROUTE_USER_SPEED,
			}));
		}
	}
}
